/**
 * electronBridge.js
 *
 * Browser / Electron compatibility layer.
 * Import this from React components to safely access Electron APIs
 * without breaking the browser-based development workflow.
 */

export const isElectron = !!(
  window.electronAPI &&
  window.electronAPI.app &&
  window.electronAPI.app.isElectron
);

// Rollback switch for openspec/changes/web-server-source-of-truth's web
// debounced push / flush-on-close / relaxed active-typing sync skip.
// Defaults on; set REACT_APP_WEB_AGGRESSIVE_SYNC=false to revert web to its
// previous IndexedDB-only-until-the-next-periodic-tick behavior without a
// code change. Electron is unaffected either way -- it never reads this.
export const isWebAggressiveSyncEnabled = process.env.REACT_APP_WEB_AGGRESSIVE_SYNC !== 'false';

// No-op stubs for browser mode
const noopAsync = async () => null;
const noopSync = () => {};

export const electronAPI = isElectron
  ? window.electronAPI
  : {
      window: {
        minimize: noopSync,
        maximize: noopSync,
        close: noopSync,
        isMaximized: noopAsync,
      },
      fs: {
        readFile: noopAsync,
        writeFile: noopAsync,
        readExternalFile: noopAsync,
        chooseFolder: noopAsync,
        listDirectory: noopAsync,
        ensureDir: noopAsync,
        deleteFile: noopAsync,
        deleteDir: noopAsync,
        watchFolder: noopAsync,
        setProjectFolder: noopAsync,
        onExternalChange: () => () => {},
      },
      server: {
        start: noopAsync,
        stop: noopAsync,
        status: noopAsync,
        getPort: noopAsync,
      },
      cardIngest: {
        openWindow: noopSync,
        listDevices: noopAsync,
        scan: noopAsync,
        chooseDestination: noopAsync,
        chooseSourceFolder: noopAsync,
        startOffload: noopAsync,
        cancelOffload: noopAsync,
        listJobs: noopAsync,
        listTemplates: noopAsync,
        saveTemplate: noopAsync,
        deleteTemplate: noopAsync,
        resolveTemplate: noopAsync,
        chooseProjectRoot: noopAsync,
        onDetected: () => () => {},
        onRemoved: () => () => {},
        onProgress: () => () => {},
        onComplete: () => () => {},
      },
      proxyQueue: {
        enqueue: noopAsync,
        getQueue: noopAsync,
        cancelJob: noopAsync,
        getEngineStatus: noopAsync,
        getEnginePreferences: noopAsync,
        setEnginePreference: noopAsync,
        getSystemInfo: noopAsync,
        onProgress: () => () => {},
        onJobStarted: () => () => {},
        onJobComplete: () => () => {},
        onQueueComplete: () => () => {},
      },
      driveSync: {
        getAuthStatus: noopAsync,
        connect: noopAsync,
        disconnect: noopAsync,
        listDriveFolders: noopAsync,
        listMappings: noopAsync,
        createMapping: noopAsync,
        deleteMapping: noopAsync,
        chooseLocalFolder: noopAsync,
        enqueueUpload: noopAsync,
        syncNow: noopAsync,
        listConflicts: noopAsync,
        resolveConflict: noopAsync,
        getQueue: noopAsync,
        cancelJob: noopAsync,
        getUploadBudget: noopAsync,
        getUploadConcurrency: noopAsync,
        setUploadConcurrency: noopAsync,
        writeReport: noopAsync,
        onProgress: () => () => {},
        onJobStarted: () => () => {},
        onJobComplete: () => () => {},
      },
      updater: {
        onStatus: () => () => {},
        onProgress: () => () => {},
        download: noopAsync,
        install: noopSync,
      },
      auth: {
        getToken: noopAsync,
        onSignedIn: () => () => {},
      },
      app: {
        isElectron: false,
        platform: 'browser',
        version: noopAsync,
      },
    };
